import { Box, Checkbox, FormControl, IconButton, InputAdornment, ListItemText, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { PaginatedDateRangeFilter } from "../PaginatedDateRangeFilter";
import { Search } from "@mui/icons-material";
import { useState } from "react";
import { SelectChangeEvent } from "@mui/material/Select";
import { Download } from "react-feather";

interface Plan {
    id: string;
    name: string;
}

interface Account {
    id: string;
    name: string;
}

interface TransactionsTableControlBarProps {
    startDate: Date | undefined;
    endDate: Date | undefined;
    updateStartDate: (date: Date | undefined) => void;
    updateEndDate: (date: Date | undefined) => void;
    onSearch: (searchTerm: string) => void;
    searchTerm?: string;
    plans?: Plan[];
    selectedPlans?: string[];
    onPlansChange?: (planIds: string[]) => void;
    accounts?: Account[];
    selectedAccounts?: string[];
    onAccountsChange?: (accountIds: string[]) => void;
    onExportCSV?: () => void;
    onExportTaxLabFbt?: () => void;
}

export const TransactionsTableControlBar: React.FC<TransactionsTableControlBarProps> = ({
    startDate,
    endDate,
    updateStartDate,
    updateEndDate,
    onSearch,
    searchTerm = "",
    plans = [],
    selectedPlans = [],
    onPlansChange,
    accounts = [],
    selectedAccounts = [],
    onAccountsChange,
    onExportCSV,
    onExportTaxLabFbt
}) => {
    const [searchInput, setSearchInput] = useState(searchTerm);
    const [exportValue, setExportValue] = useState<string>("");

    const handleSearch = () => {
        onSearch(searchInput);
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const handlePlanChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value;
        onPlansChange?.(typeof value === 'string' ? [value] : value);
    };

    const handleAccountChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value;
        onAccountsChange?.(typeof value === 'string' ? [value] : value);
    };

    const handleExportChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        setExportValue("");  // Reset the select value
        
        if (value === "csv") {
            onExportCSV?.();
        } else if (value === "taxlab") {
            onExportTaxLabFbt?.();
        }
    };

    return (
        <Box display="flex" width="100%" justifyContent="space-between" mt={4}>
            <Stack direction="row" spacing={2} my={2}>
                <PaginatedDateRangeFilter
                    startDate={startDate}
                    endDate={endDate}
                    updateStartDate={updateStartDate}
                    updateEndDate={updateEndDate}
                />
                {plans.length > 0 && onPlansChange && (
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <Select
                            multiple
                            value={selectedPlans}
                            onChange={handlePlanChange}
                            displayEmpty
                            renderValue={(selected) => {
                                if (selected.length === 0) {
                                    return 'Account';
                                }
                                return plans
                                    .filter(plan => selected.includes(plan.id))
                                    .map(plan => plan.name)
                                    .join(', ');
                            }}
                        >
                            {plans.map((plan) => (
                                <MenuItem key={plan.id} value={plan.id}>
                                    <Checkbox checked={selectedPlans.includes(plan.id)} />
                                    <ListItemText primary={plan.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                {accounts.length > 0 && onAccountsChange && (
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <Select
                            multiple
                            value={selectedAccounts}
                            onChange={handleAccountChange}
                            displayEmpty
                            renderValue={(selected) => {
                                if (selected.length === 0) {
                                    return 'Account';
                                }
                                return accounts
                                    .filter(account => selected.includes(account.id))
                                    .map(account => account.name)
                                    .join(', ');
                            }}
                        >
                            {accounts.map((account) => (
                                <MenuItem key={account.id} value={account.id}>
                                    <Checkbox checked={selectedAccounts.includes(account.id)} />
                                    <ListItemText primary={account.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </Stack>
            <Stack direction="row" spacing={2} my={2} mr={2} alignItems="center">
                <TextField
                    placeholder="Search"
                    size="small"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={handleSearch} edge="end">
                                    <Search />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                {(onExportCSV || onExportTaxLabFbt) && (
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                            value={exportValue}
                            onChange={handleExportChange}
                            displayEmpty
                            renderValue={() => (
                                <Box display="flex" alignItems="center">
                                    <Download size={18} style={{ marginRight: 8 }} />
                                    Export
                                </Box>
                            )}
                        >
                            {onExportTaxLabFbt && (
                                <MenuItem value="taxlab">
                                    <Box display="flex" alignItems="center">
                                        <Typography>TaxLab FBT Extract</Typography>
                                    </Box>
                                </MenuItem>
                            )}
                            {onExportCSV && (
                                <MenuItem value="csv">
                                    <Box display="flex" alignItems="center">
                                        <Typography>Export CSV</Typography>
                                    </Box>
                                </MenuItem>
                            )}
                        </Select>
                    </FormControl>
                )}
            </Stack>
        </Box>
    );
}; 
